import { Box, Popover, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { FormCheck, Button } from "react-bootstrap";
import { OrganisationDto } from "types";

// Filter popup component
interface FilterPopupProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onApplyFilter: (filters: FilterState) => void;
    orgList: OrganisationDto[];
    userEmail: string;
    personalAccountLabel: string;
    currentFilters: FilterState;
}

export interface FilterState {
    search: string;
    unreadOnly: boolean;
    selectedOrganizations: string[];
}

const StyledFilterPopover = styled(Popover)`
    & .MuiPaper-root {
        background-color: #ffffff;
        border: none;
        border-radius: 8px;
        width: 300px;
        max-height: 500px;
        color: #2f3a51;
    }
`;

const ScrollableSection = styled(Box)`
    max-height: 150px;
    overflow-y: auto;
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background-color: #f0f0f0;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #c0c0c0;
        border-radius: 3px;
    }
`;

const CustomCheck = styled(FormCheck)`
    .custom-control-label:before {
        background-color: #dddddd;
        border: 1px solid #dddddd;
    }
`;

export const FilterPopup = React.memo(
    ({
        open,
        anchorEl,
        onClose,
        onApplyFilter,
        orgList,
        userEmail,
        personalAccountLabel,
        currentFilters,
    }: FilterPopupProps) => {
        const [filters, setFilters] = React.useState<FilterState>({
            search: "",
            unreadOnly: false,
            selectedOrganizations: [],
        });

        const [organizationSearch, setOrganizationSearch] = React.useState("");

        // Sync internal state with parent's currentFilters
        React.useEffect(() => {
            setFilters(currentFilters);
        }, [currentFilters]);

        // One pass to both apply search matching and prepare grouped rows for rendering.
        const { primaryOrgs, anotherOrgs } = React.useMemo(() => {
            const normalizedSearch = organizationSearch.trim().toLowerCase();
            const grouped = {
                primaryOrgs: [] as OrganisationDto[],
                anotherOrgs: [] as OrganisationDto[],
            };

            orgList.forEach((org) => {
                const isPersonalOrg = org.isDefault && org.owner === userEmail;
                const displayName = isPersonalOrg
                    ? personalAccountLabel
                    : org.name;

                const hasSearch = normalizedSearch.length > 0;
                const matchesSearch = displayName
                    .toLowerCase()
                    .includes(normalizedSearch);

                // If there is a search and the organization does not match the search, skip it.
                if (hasSearch && !matchesSearch) {
                    return;
                }

                if (isPersonalOrg) {
                    grouped.primaryOrgs.push(org);
                    return;
                }

                grouped.anotherOrgs.push(org);
            });

            return grouped;
        }, [orgList, organizationSearch, userEmail, personalAccountLabel]);

        const handleSearchChange = (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            setOrganizationSearch(event.target.value);
        };

        const handleOrganizationChange = (
            orgUuid: string,
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            setFilters((prev) => ({
                ...prev,
                selectedOrganizations: event.target.checked
                    ? [...prev.selectedOrganizations, orgUuid]
                    : prev.selectedOrganizations.filter((o) => o !== orgUuid),
            }));
        };

        const handleClear = () => {
            const clearedFilters = {
                search: "",
                unreadOnly: false,
                selectedOrganizations: [],
            };
            setFilters(clearedFilters);
            setOrganizationSearch("");
            onApplyFilter(clearedFilters);
            onClose();
        };

        const handleApply = () => {
            onApplyFilter(filters);
            onClose();
        };

        return (
            <StyledFilterPopover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Box sx={{ padding: "16px" }}>
                    {/* Title */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 500,
                            fontSize: "16px",
                            marginBottom: "16px",
                        }}
                    >
                        Filter
                    </Typography>

                    {/* Search Input */}
                    <TextField
                        fullWidth
                        placeholder="Search..."
                        value={organizationSearch}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{
                            marginBottom: "16px",
                            backgroundColor: "#F0F0F0 !important",
                            borderRadius: "4px",
                            "& .MuiInputBase-input": {
                                color: "#2F3A51",
                            },
                            "& .MuiInputBase-input::placeholder": {
                                color: "#828FAB",
                                opacity: 1,
                            },
                        }}
                    />


                    <ScrollableSection>
                        {primaryOrgs.length > 0 && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        color: "#828FAB",
                                        marginBottom: "16px",
                                    }}
                                >
                                    Personal Account
                                </Typography>
                                {primaryOrgs.map((org) => (
                                    <CustomCheck
                                        key={org.uuid}
                                        className="mb-2"
                                        name="selected-sensor"
                                        value={org.uuid}
                                        checked={filters.selectedOrganizations.includes(
                                            org.uuid
                                        )}
                                        custom
                                        onChange={(e) =>
                                            handleOrganizationChange(org.uuid, e)
                                        }
                                        id={org.uuid}
                                        label={
                                            <span className="ml-2">
                                                {personalAccountLabel}
                                            </span>
                                        }
                                    />
                                ))}
                            </>
                        )}
                        {anotherOrgs.length > 0 && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        color: "#828FAB",
                                        marginBottom: "16px",
                                    }}
                                >
                                    Organisations
                                </Typography>
                                {anotherOrgs.map((org) => (
                                    <CustomCheck
                                        key={org.uuid}
                                        className="mb-2"
                                        name="selected-sensor"
                                        value={org.uuid}
                                        checked={filters.selectedOrganizations.includes(
                                            org.uuid
                                        )}
                                        custom
                                        onChange={(e) =>
                                            handleOrganizationChange(org.uuid, e)
                                        }
                                        id={org.uuid}
                                        label={
                                            <span className="ml-2">
                                                {org.name}
                                            </span>
                                        }
                                    />
                                ))}
                            </>
                        )}
                    </ScrollableSection>

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            marginTop: "16px",
                            display: "flex",
                            gap: "8px",
                            justifyContent: "space-between",
                        }}
                    >
                        <Button
                            variant="dark"
                            onClick={handleClear}
                            size="sm"
                            className="px-md-3"
                        >
                            CLEAR
                        </Button>
                        <Button
                            className="px-md-3"
                            variant="primary"
                            onClick={handleApply}
                            size="sm"
                        >
                            APPLY
                        </Button>
                    </Box>
                </Box>
            </StyledFilterPopover>
        );
    }
);
